import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, KeyRound, Search, Trash2, FileText, IndianRupee, Users, CalendarDays, CheckCircle2, TrendingUp, ExternalLink } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api, { formatINR, fileUrl, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Crest, Loader } from "../components/bits";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { RosterManager } from "../components/RosterManager";

const timeIST = (iso) => new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const plural = (n, w) => `${n} ${w}${n === 1 ? "y" : "ies"}`;
const dayLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

const Stat = ({ icon: Icon, label, value, sub, testid, dark }) => (
  <div className={`rounded-lg border p-4 sm:p-5 ${dark ? "bg-stone-900 text-stone-50 border-stone-900" : "bg-white border-stone-300/70"}`} data-testid={testid}>
    <Icon className={`h-5 w-5 ${dark ? "text-amber-400" : "text-amber-700"}`} />
    <p className="font-display text-2xl sm:text-3xl font-bold mt-3 break-words">{value}</p>
    <p className={`eyebrow mt-1 !text-[0.68rem] ${dark ? "!text-amber-300" : ""}`}>{label}</p>
    {sub && <p className={`text-xs mt-1 ${dark ? "text-stone-400" : "text-stone-500"}`}>{sub}</p>}
  </div>
);

const DailyChart = ({ data }) => (
  <div className="bg-white border border-stone-300/70 rounded-lg p-4 sm:p-6" data-testid="contributions-daily-chart">
    <h3 className="font-bold text-lg mb-4">Collected by date</h3>
    {data.length === 0 ? <p className="text-sm text-stone-500">No data yet.</p> : (
      <div className="h-64 min-w-0">
        <ResponsiveContainer width="100%" height={256} minWidth={200}>
          <BarChart data={data.map((d) => ({ ...d, label: dayLabel(d.date) }))} margin={{ left: -10, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E9DFCE" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6F5E4C" }} />
            <YAxis tick={{ fontSize: 11, fill: "#6F5E4C" }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
            <Tooltip formatter={(v, n) => (n === "amount" ? [formatINR(v), "Amount"] : [v, "Entries"])} contentStyle={{ background: "#FDFBF7", border: "1px solid #DACAB4", borderRadius: 6 }} />
            <Bar dataKey="amount" fill="#A85E07" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}
    {data.length > 0 && (
      <div className="mt-4 divide-y divide-stone-200 text-sm" data-testid="contributions-daily-table">
        {[...data].reverse().map((d) => (
          <div key={d.date} className="flex justify-between py-2"><span>{dayLabel(d.date)}</span><span className="text-stone-500">{plural(d.count, "entr")}</span><span className="font-semibold">{formatINR(d.amount)}</span></div>
        ))}
      </div>
    )}
  </div>
);

const ProofButton = ({ c, onOpen }) => (
  !c.screenshot_id ? <span className="text-xs text-stone-400 font-typewriter" data-testid={`proof-none-${c.id}`}>No proof</span>
  : c.screenshot_type === "application/pdf"
    ? <a href={fileUrl(c.screenshot_id)} target="_blank" rel="noreferrer" data-testid={`proof-open-${c.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 underline"><FileText className="h-4 w-4" />PDF</a>
    : <button onClick={() => onOpen(c)} data-testid={`proof-open-${c.id}`}><img src={fileUrl(c.screenshot_id)} alt="proof" loading="lazy" className="h-12 w-12 object-cover rounded border border-stone-300 hover:ring-2 hover:ring-amber-500" /></button>
);

const EntryActions = ({ c, onVerify, onDelete }) => (
  <div className="flex items-center gap-3">
    <label className="flex items-center gap-2 text-xs font-semibold text-stone-600">
      <Switch checked={c.verified} onCheckedChange={(v) => onVerify(c, v)} data-testid={`verify-switch-${c.id}`} />{c.verified ? "Verified" : "Verify"}
    </label>
    <button onClick={() => onDelete(c)} data-testid={`delete-entry-${c.id}`} className="p-1.5 rounded text-stone-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
  </div>
);

const AnonBadge = ({ id }) => (
  <span data-testid={id} title="Hidden from beneficiary — shown as Anonymous in exports"
    className="ml-2 inline-block align-middle rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">Anonymous</span>
);

const EntriesList = ({ rows, onOpen, onVerify, onDelete }) => (
  <>
    <div className="hidden md:block overflow-x-auto bg-white border border-stone-300/70 rounded-lg">
      <table className="w-full text-sm" data-testid="contributions-table">
        <thead className="bg-stone-100 text-left font-typewriter text-xs uppercase text-stone-600">
          <tr><th className="p-3">When (IST)</th><th className="p-3">Name</th><th className="p-3">Mobile</th><th className="p-3">Email</th><th className="p-3 text-right">Amount</th><th className="p-3">Proof</th><th className="p-3">Status</th></tr>
        </thead>
        <tbody className="divide-y divide-stone-200">
          {rows.map((c) => (
            <tr key={c.id} data-testid={`contribution-row-${c.id}`} className="hover:bg-stone-50">
              <td className="p-3 whitespace-nowrap text-stone-600">{timeIST(c.created_at)}</td>
              <td className="p-3 font-semibold">{c.name}{c.anonymous && <AnonBadge id={`anon-label-${c.id}`} />}</td>
              <td className="p-3 whitespace-nowrap">{c.mobile ? <a href={`tel:${c.mobile}`} className="hover:underline">{c.mobile}</a> : "—"}</td>
              <td className="p-3 text-stone-600 break-all">{c.email || "—"}</td>
              <td className="p-3 text-right font-display font-bold text-base whitespace-nowrap">{formatINR(c.amount)}</td>
              <td className="p-3"><ProofButton c={c} onOpen={onOpen} /></td>
              <td className="p-3"><EntryActions c={c} onVerify={onVerify} onDelete={onDelete} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="md:hidden space-y-3" data-testid="contributions-cards">
      {rows.map((c) => (
        <div key={c.id} className="bg-white border border-stone-300/70 rounded-lg p-4" data-testid={`contribution-card-${c.id}`}>
          <div className="flex gap-3">
            <ProofButton c={c} onOpen={onOpen} />
            <div className="flex-1 min-w-0">
              <div className="flex justify-between gap-2"><p className="font-semibold truncate">{c.name}{c.anonymous && <AnonBadge id={`anon-label-mobile-${c.id}`} />}</p><p className="font-display font-bold">{formatINR(c.amount)}</p></div>
              <p className="text-xs text-stone-600"><a href={`tel:${c.mobile}`}>{c.mobile}</a> • <span className="break-all">{c.email}</span></p>
              <p className="text-xs text-stone-500 font-typewriter mt-0.5">{timeIST(c.created_at)}</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-stone-100"><EntryActions c={c} onVerify={onVerify} onDelete={onDelete} /></div>
        </div>
      ))}
    </div>
  </>
);

const ByPerson = ({ people }) => (
  <div className="bg-white border border-stone-300/70 rounded-lg overflow-x-auto" data-testid="contributions-by-person">
    <table className="w-full text-sm">
      <thead className="bg-stone-100 text-left font-typewriter text-xs uppercase text-stone-600">
        <tr><th className="p-3">#</th><th className="p-3">Name</th><th className="p-3">Mobile</th><th className="p-3 text-center">Entries</th><th className="p-3 text-right">Total</th></tr>
      </thead>
      <tbody className="divide-y divide-stone-200">
        {people.map((p, i) => (
          <tr key={p.mobile + i}><td className="p-3 text-stone-500">{i + 1}</td><td className="p-3 font-semibold">{p.name}{p.anonymous && <AnonBadge id={`anon-person-${i}`} />}</td><td className="p-3 whitespace-nowrap">{p.mobile || "—"}</td><td className="p-3 text-center">{p.count}</td><td className="p-3 text-right font-display font-bold whitespace-nowrap">{formatINR(p.amount)}</td></tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ChangePassword = () => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const set = (k) => (e) => setF({ ...f, [k]: k === "current_password" ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6) });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("New PINs don't match");
    try {
      await api.post("/admin/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Admin PIN updated");
      setOpen(false);
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatError(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button onClick={() => setOpen(true)} data-testid="admin-change-password" className="p-2 rounded-full hover:bg-stone-200" title="Change PIN"><KeyRound className="h-5 w-5" /></button>
      <DialogContent className="bg-stone-50 max-w-sm">
        <DialogTitle className="font-display text-2xl">Change admin PIN</DialogTitle>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div className="space-y-1.5"><Label>Current PIN</Label><Input type="password" inputMode="numeric" required value={f.current_password} onChange={set("current_password")} data-testid="current-password-input" /></div>
          <div className="space-y-1.5"><Label>New 6-digit PIN</Label><Input type="password" inputMode="numeric" required minLength={6} pattern="\d{6}" value={f.new_password} onChange={set("new_password")} className="tracking-[0.5em] font-mono" data-testid="new-password-input" /></div>
          <div className="space-y-1.5"><Label>Confirm new PIN</Label><Input type="password" inputMode="numeric" required minLength={6} pattern="\d{6}" value={f.confirm} onChange={set("confirm")} className="tracking-[0.5em] font-mono" data-testid="confirm-password-input" /></div>
          <button type="submit" data-testid="change-password-submit" className="w-full h-11 rounded-full bg-stone-900 text-stone-50 font-semibold">Save PIN</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function ContributionsAdmin() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [tab, setTab] = useState("entries");
  const [proof, setProof] = useState(null);

  const load = () => {
    const onFail = async (err) => {
      if ([401, 403].includes(err?.response?.status)) {
        await logout().catch(() => {});
        toast.error("Session expired. Please enter the PIN again.");
        navigate("/adminlogin", { replace: true });
      } else {
        toast.error(formatError(err));
      }
    };
    api.get("/admin/contributions").then(({ data }) => setRows(data)).catch(onFail);
    api.get("/admin/contributions/stats").then(({ data }) => setStats(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => (rows || []).filter((c) => {
    const s = q.toLowerCase();
    const hit = !s || c.name.toLowerCase().includes(s) || c.mobile.includes(s) || c.email.includes(s);
    return hit && (!from || c.date >= from) && (!to || c.date <= to);
  }), [rows, q, from, to]);
  const filteredTotal = filtered.reduce((a, c) => a + c.amount, 0);

  const verify = async (c, verified) => {
    await api.patch(`/admin/contributions/${c.id}`, { verified, admin_note: c.admin_note || "" });
    load();
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}'s entry of ${formatINR(c.amount)}?`)) return;
    await api.delete(`/admin/contributions/${c.id}`);
    toast.success("Entry deleted");
    load();
  };
  const exportFile = async (ext) => {
    try {
      const { data } = await api.get(`/admin/contributions/export.${ext}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lvb-contributions-${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatError(err));
    }
  };
  const doLogout = async () => { await logout(); navigate("/adminlogin"); };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 bg-stone-50/95 backdrop-blur-md border-b border-stone-300/70" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-3">
          <Link to="/filltheform" className="flex items-center gap-2.5 min-w-0" data-testid="admin-home-link">
            <Crest />
            <span className="leading-none min-w-0">
              <span className="font-display font-bold text-lg block truncate">Contributions — One of us needs help</span>
              <span className="font-typewriter text-[10px] uppercase tracking-widest text-amber-800">LVB Hostel • Admin</span>
            </span>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => exportFile("csv")} data-testid="admin-export-csv" className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full bg-stone-900 text-stone-50 text-xs sm:text-sm font-semibold"><Download className="h-4 w-4" />CSV</button>
            <button onClick={() => exportFile("pdf")} data-testid="admin-export-pdf" className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full bg-amber-700 text-stone-50 text-xs sm:text-sm font-semibold"><FileText className="h-4 w-4" />PDF</button>
            <ChangePassword />
            <button onClick={doLogout} data-testid="admin-logout" className="p-2 rounded-full hover:bg-stone-200" title="Log out"><LogOut className="h-5 w-5" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {!stats || !rows ? <Loader /> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <Stat dark icon={IndianRupee} label="Total recorded" value={formatINR(stats.total)} sub={`${formatINR(stats.verified_total)} verified`} testid="stat-total" />
              <Stat icon={Users} label="Contributors" value={stats.contributors} sub={plural(stats.count, "entr")} testid="stat-contributors" />
              <Stat icon={CalendarDays} label="Today" value={formatINR(stats.today_total)} sub={plural(stats.today_count, "entr")} testid="stat-today" />
              <Stat icon={TrendingUp} label="Average" value={formatINR(stats.average)} sub={`Highest ${formatINR(stats.highest)}`} testid="stat-average" />
            </div>
            {stats.pending_verification > 0 && (
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-2" data-testid="pending-verification-note">
                <CheckCircle2 className="h-4 w-4" />{plural(stats.pending_verification, "entr")} not yet verified against your bank/UPI statement.
              </p>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
              <div className="lg:col-span-2 space-y-4">
                <div className="flex gap-2">
                  {[["entries", "All entries"], ["people", "By contributor"], ["roster", "Name list"]].map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === k ? "bg-stone-900 text-stone-50" : "bg-white border border-stone-300 text-stone-600"}`}>{l}</button>
                  ))}
                </div>
                {tab === "entries" ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                      <div className="relative sm:col-span-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile, email" className="pl-9 h-10 bg-white" data-testid="admin-search-input" />
                      </div>
                      <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 bg-white" data-testid="admin-from-date" />
                      <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 bg-white" data-testid="admin-to-date" />
                    </div>
                    <p className="text-sm text-stone-600" data-testid="admin-filtered-summary">{plural(filtered.length, "entr")} • <span className="font-semibold text-stone-900">{formatINR(filteredTotal)}</span></p>
                    {filtered.length === 0 ? <p className="text-sm text-stone-500 py-10 text-center border border-dashed border-stone-300 rounded-lg" data-testid="admin-empty">No contributions yet.</p>
                      : <EntriesList rows={filtered} onOpen={setProof} onVerify={verify} onDelete={remove} />}
                  </>
                ) : tab === "people" ? <ByPerson people={stats.by_person} /> : <RosterManager />}
              </div>
              <DailyChart data={stats.by_date} />
            </div>
          </>
        )}
      </main>

      <Dialog open={!!proof} onOpenChange={(v) => !v && setProof(null)}>
        <DialogContent className="max-w-2xl bg-stone-50 p-3">
          <DialogTitle className="font-display px-1">{proof?.name} — {proof && formatINR(proof.amount)}</DialogTitle>
          {proof && (
            <>
              <img src={fileUrl(proof.screenshot_id)} alt="Payment proof" className="w-full max-h-[75vh] object-contain rounded" data-testid="proof-dialog-image" />
              <a href={fileUrl(proof.screenshot_id)} target="_blank" rel="noreferrer" className="text-sm font-semibold text-amber-800 inline-flex items-center gap-1 px-1"><ExternalLink className="h-4 w-4" />Open full size</a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
